import { Modal } from 'antd';
import { DATE_FORMATS, NPS_CATEGORY } from './constants';
import { captureException } from './sentryUtils';
import { getTenant } from './storageUtils';
import {
  AccountDetails,
  AccountStatusType,
  FIType,
  FIStatus,
  NPSSchemeCategory,
  RefreshDataFetchStatus,
  TenantType,
} from './types';

const months = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// Function to format date to "DD-MM-YYYY"
function formatDate(dateStr: any) {
  return dayjs(dateStr).format('DD MMM YYYY');
}

// Grouping function
function groupByDate(transactions: any) {
  if (!transactions || !Array.isArray(transactions)) {
    return {};
  }
  return transactions.reduce((acc: any, transaction: any) => {
    const formattedDate = formatDate(transaction.dateOfTransaction);
    if (!acc[formattedDate]) {
      acc[formattedDate] = [];
    }
    acc[formattedDate].push(transaction);
    return acc;
  }, {});
}

// Sorting function
function sortGroupedTransactions(groupedTransactions: any) {
  const sortedKeys = Object.keys(groupedTransactions).sort((a, b) => {
    const dateA: any = new Date(a.split('-').reverse().join('-'));
    const dateB: any = new Date(b.split('-').reverse().join('-'));
    return dateB - dateA;
  });

  const sortedGroupedTransactions: any = {};
  sortedKeys.forEach((key) => {
    sortedGroupedTransactions[key] = groupedTransactions[key];
  });

  return sortedGroupedTransactions;
}

export function groupByTransactionsCredit(transaction: any) {
  const groupedTransactions = groupByDate(transaction);
  const sortedGroupedTransactions =
    sortGroupedTransactions(groupedTransactions);

  return sortedGroupedTransactions;
}

export function mergeObjects(obj1: any, obj2: any) {
  const result = { ...obj1 };

  for (const key in obj2) {
    if (result[key]) {
      result[key] = result[key].concat(obj2[key]);
    } else {
      result[key] = obj2[key];
    }
  }

  return result;
}
export function groupByTransactionsBasedOnTxn(transaction: any) {
  const groupedTransactions = groupByDate(transaction);
  const sortedGroupedTransactions =
    sortGroupedTransactions(groupedTransactions);

  return sortedGroupedTransactions;
}

export function getLastSixMonths() {
  const months = [];
  const now = new Date();

  for (let i = 0; i < 6; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);

    const month = date.toLocaleString('default', { month: 'short' }); // 'Sep'
    const year = date.getFullYear(); // 2023

    const value = `${month.toLowerCase()}${year}`; // 'sep2023'
    const label = `${month} ${year}`; // 'Sep 2023'

    months.push({ value, label });
  }

  return months;
}
export const capitalizeWords = (str: string) => {
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Strips characters that have no business in a plain display name/note field
// but are commonly used to break out into markup or code context downstream
// (HTML/script tags via < >, template-literal/SSTI payloads via ` ${ },
// escape-sequence tricks via \). Caps length so a single field can't be used
// to send an oversized payload to the server. Ordinary punctuation (', -, &,
// (), ., ,) is left untouched since legitimate names rely on it.
const UNSAFE_TEXT_INPUT_PATTERN = /[<>`${}\\]/g;

export const sanitizeTextInput = (value: string, maxLength: number): string =>
  value.replace(UNSAFE_TEXT_INPUT_PATTERN, '').slice(0, maxLength);

export function maskEmail(email: string): string {
  if (!email || !email.includes('@')) {
    return 'N/A';
  }
  const [username, domain] = email.split('@');

  if (username.length > 5) {
    const maskedUsername =
      username.slice(0, 2) +
      '*'.repeat(username.length - 3) +
      username.slice(-1);
    return maskedUsername + '@' + domain;
  } else {
    const maskedUsername =
      username.slice(0, 1) +
      '*'.repeat(username.length - 2) +
      username.slice(-1);
    return maskedUsername + '@' + domain;
  }
}

export function convertAmoutToINRWithNotation_MF(
  amount?: number, // Marking amount as optional to handle undefined cases
  toFixedCount: number = 2 // Providing default value
): string {
  if (amount == null || isNaN(amount)) {
    return '₹0.00'; // Handle undefined, null, or NaN cases
  }

  if (amount >= 1e7) {
    return `₹${(amount / 1e7).toFixed(toFixedCount)} Cr`;
  } else if (amount >= 1e5) {
    return `₹${(amount / 1e5).toFixed(toFixedCount)} L`;
  } else if (amount >= 1e3) {
    return `₹${(amount / 1e3).toFixed(toFixedCount)} K`;
  } else {
    return `₹${amount.toFixed(toFixedCount)}`;
  }
}

export function convertAmoutToINRWithNotation(
  amount: number,
  toFixedCount?: number
) {
  if (amount >= Math.pow(10, 7)) {
    return (amount / Math.pow(10, 7)).toFixed(toFixedCount ?? 2) + ' Cr';
  } else if (amount >= Math.pow(10, 5)) {
    return (amount / Math.pow(10, 5)).toFixed(toFixedCount ?? 2) + ' L';
  } else if (amount >= Math.pow(10, 3)) {
    return (amount / Math.pow(10, 3)).toFixed(toFixedCount ?? 2) + ' K';
  } else {
    return amount.toFixed(2);
  }
}

export function formatToINR(
  amountStr: string,
  isVisible: boolean = true,
  decimalAllowed: number = 0
) {
  // Parse the string to a number
  if (!isVisible) {
    if (getTenant() === TenantType.JFS) {
      return '•••••';
    } else {
      return '****';
    }
  } else if (amountStr === '') {
    amountStr = '0';
  }
  var amount = parseFloat(amountStr);
  if (Number.isNaN(amount)) {
    amount = 0;
  }
    if (decimalAllowed === 0) {
    decimalAllowed = amount < 1 ? 2 : 0;
  }
  const isSIB = getTenant() === TenantType.SIB;
  const fractionDigits = isSIB ? 2 : decimalAllowed;
  // Create a NumberFormat instance for the Indian locale with currency style
  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

  // Format the number as INR currency
  return formatter.format(amount);
}

const AMOUNT_WORD_ONES = [
  '', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
  'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
  'seventeen', 'eighteen', 'nineteen',
];
const AMOUNT_WORD_TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

const belowHundred = (n: number): string => {
  if (n < 20) return AMOUNT_WORD_ONES[n];
  const t = Math.floor(n / 10);
  const o = n % 10;
  return o ? `${AMOUNT_WORD_TENS[t]} ${AMOUNT_WORD_ONES[o]}` : AMOUNT_WORD_TENS[t];
};

const belowThousand = (n: number): string => {
  const h = Math.floor(n / 100);
  const rest = n % 100;
  const parts: string[] = [];
  if (h) parts.push(`${AMOUNT_WORD_ONES[h]} hundred`);
  if (rest) parts.push(belowHundred(rest));
  return parts.join(' ');
};

// Indian numbering system → words, e.g. 500000 -> "five lakh".
const amountToIndianWords = (amount: number): string => {
  if (!Number.isFinite(amount) || amount <= 0) return '';
  let num = Math.floor(amount);
  const crore = Math.floor(num / 10000000);
  num %= 10000000;
  const lakh = Math.floor(num / 100000);
  num %= 100000;
  const thousand = Math.floor(num / 1000);
  num %= 1000;
  const parts: string[] = [];
  if (crore) parts.push(`${belowThousand(crore)} crore`);
  if (lakh) parts.push(`${belowHundred(lakh)} lakh`);
  if (thousand) parts.push(`${belowHundred(thousand)} thousand`);
  if (num) parts.push(belowThousand(num));
  return parts.join(' ');
};

// Amount to a display sentence, e.g. 500000 -> "Five lakh rupees only".
export const amountToWordsINR = (amount: number): string => {
  const words = amountToIndianWords(amount);
  if (!words) return '';
  const sentence = `${words} rupees only`;
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
};

// Indian digit grouping for display, e.g. "500000" -> "5,00,000".
export const groupDigitsIndian = (digits: string): string => {
  const clean = digits.replace(/^0+(?=\d)/, '');
  if (!clean) return '';
  const last3 = clean.slice(-3);
  const rest = clean.slice(0, -3);
  if (!rest) return last3;
  return `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${last3}`;
};

export function formatTransactionAmount(
  amountStr: string,
  transactionType: string,
  isVisible: boolean = true
) {
  // Parse the string to a number
  if (!isVisible) {
    return '•••••';
  } else if (amountStr === '') {
    amountStr = '0';
  }

  var amount = parseFloat(amountStr);
  if (Number.isNaN(amount)) {
    amount = 0;
  }

  const isSIB = getTenant() === TenantType.SIB;
  const fractionDigits = isSIB ? 2 : (amount < 1 ? 2 : 0);
  // Create a NumberFormat instance for the Indian locale with currency style
  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

  // Format the number as INR currency
  const formattedAmount = formatter.format(Math.abs(amount));

  // Add + for credit/incoming transactions, - for debit/outgoing transactions
  const isCredit =
    transactionType && transactionType.toLowerCase().startsWith('c');
  const prefix = isCredit ? '+' : '-';

  return `${prefix}${formattedAmount}`;
}

export function formatToINRForStocks(
  amountStr: string,
  isVisible: boolean = true
) {
  // Parse the string to a number
  if (!isVisible) {
    return '****';
  } else if (amountStr === '') {
    amountStr = '0';
  }
  var amount = parseFloat(amountStr);
  if (Number.isNaN(amount)) {
    amount = 0;
  }
  const isSIB = getTenant() === TenantType.SIB;
  const fractionDigits = isSIB ? 2 : (amount < 1 ? 2 : 0);
  // Create a NumberFormat instance for the Indian locale with currency style
  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'decimal',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

  // Format the number as INR currency
  return formatter.format(amount);
}
export type monthlyCreditType = {
  year: string;
  amount: number;
  monthlyData: {
    month: string;
    amount: number;
  }[];
};

export function getMonthAbbreviation(monthNumber: number) {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  // Subtract 1 from the monthNumber because array indices start at 0
  return months[monthNumber - 1];
}

export function getMonthlyDebitCredit(accP: any, accountsData?: any) {
  return accountsData.accountProfileList
    .filter((acc: any) => {
      return acc.account.number.slice(-4).includes(accP.accNo.slice(-4));
    })[0]
    .monthlyAnalytics.map((data: any) => {
      const month = getMonthAbbreviation(data.month);
      return {
        name: month + "'" + data.year.toString().slice(2),
        value: month.toLowerCase() + data.year,
        label: month + ' ' + data.year,
        Outflow: data.totalDebitAmount,
        Inflow: data.totalCreditAmount,
        totalDebitTxn: data.totalDebitTxn,
        totalCreditTxn: data.totalCreditTxn,
      };
    });
}

const dayjs = require('dayjs');
const customParseFormat = require('dayjs/plugin/customParseFormat');
dayjs.extend(customParseFormat);

// Date formats that FD/RD APIs may return for dateOfDeposit / dateOfMaturity.
// Tried in order via dayjs strict parsing so DD/MM/YYYY isn't misread as MM/DD/YYYY.
const FD_DATE_FORMATS = [
  'YYYY-MM-DD',
  'YYYY/MM/DD',
  'DD/MM/YYYY',
  'DD-MM-YYYY',
];

export function parseFlexibleDate(input: string | null | undefined) {
  if (!input) return null;
  const trimmed = String(input).trim();
  if (!trimmed) return null;
  const parsed = dayjs(trimmed, FD_DATE_FORMATS, true);
  return parsed.isValid() ? parsed : null;
}

export function getLastMonthsShortForm() {
  const lastMonthsShortForm = [];
  const currentDate = dayjs();

  for (let i = 0; i <= 5; i++) {
    const date = currentDate.subtract(i, 'month');
    lastMonthsShortForm.push({
      monthShortForm: date.format('MMM'),
      month: date.month() + 1,
      year: date.year(),
    });
  }
  return lastMonthsShortForm.reverse();
}

export function getFipIdByName(fipName: string) {
  switch (fipName) {
    case 'Axis Bank':
      return 'AXIS001';
    case 'HDFC Bank':
      return 'HDFC-FIP';
  }
}

export function getLast6MonthsBalanceAccountWise(accountWiseAnalytics: any) {
  const getLast6MonthDetails = getLastMonthsShortForm();
  //const accountWiseAnalytics = getAccountWiseAnalysis();
  const result: any = [];
  for (let i = 0; i < accountWiseAnalytics.length; i++) {
    for (let j = 0; j < getLast6MonthDetails.length; j++) {
      for (
        let k = 0;
        k < accountWiseAnalytics[i].monthlyAnalytics?.length;
        k++
      ) {
        if (
          getLast6MonthDetails[j].month ===
            accountWiseAnalytics[i].monthlyAnalytics[k].month &&
          getLast6MonthDetails[j].year ===
            accountWiseAnalytics[i].monthlyAnalytics[k].year
        ) {
          result.push({
            monthShortForm: getLast6MonthDetails[j].monthShortForm,
            month: getLast6MonthDetails[j].month,
            year: getLast6MonthDetails[j].year,
            totalCreditAmount:
              accountWiseAnalytics[i].monthlyAnalytics[k].totalCreditAmount,
            totalDebitAmount:
              accountWiseAnalytics[i].monthlyAnalytics[k].totalDebitAmount,
            accountNum: accountWiseAnalytics[i].account.number,
            linkedAccRef: accountWiseAnalytics[i].account.linkedAccRef,
            fipId: fipIdNameMapping[accountWiseAnalytics[i].account.bank],
            bank: accountWiseAnalytics[i].account.bank,
          });
          break;
        }
      }
    }
  }

  return {
    data: result,
    Last6Months: getLast6MonthDetails,
  };
}

export function convertToMonthYear(input: any) {
  const [year, month] = input.split('-');
  const date = new Date(year, month - 1);

  const monthName = date.toLocaleString('default', { month: 'long' });
  const yearName = date.getFullYear();

  return `${monthName} ${yearName}`;
}

export function convertDateToMonthYearNumberFormat() {
  const currentDate = new Date();
  const month = currentDate.getMonth() + 1;
  const year = currentDate.getFullYear();
  return `${month}${year}`;
}

export const getCurrentShortMonthYear = () => {
  const date = new Date();
  const month = date.toLocaleString('en-US', {
    month: 'short',
  });
  const year = date.getFullYear();
  return `${month}-${year}`;
};

const MONTH_SHORT_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export interface AvailableMonth {
  monthYear: string;
  monthShortForm: string;
  month: number;
  year: number;
}

/**
 * Collects the unique monthYear ("MMM-YY") entries present across the given
 * TOP5FUNDTRANSFER categories, so month pickers show every month that has
 * data instead of a fixed lookback window.
 */
export const getAvailableMonthsFromTop5Data = (
  top5Overall: any,
  categories: string[] = ['CREDIT', 'EXPENSE', 'INVESTMENT'],
  order: 'asc' | 'desc' = 'asc'
): AvailableMonth[] => {
  if (!top5Overall) return [];

  const monthYearSet = new Set<string>();
  categories.forEach((category) => {
    top5Overall[category]?.monthly?.forEach((m: any) => {
      if (m.monthYear) monthYearSet.add(m.monthYear);
    });
  });

  const months = Array.from(monthYearSet).map((monthYear) => {
    const [monthShortForm, yy] = monthYear.split('-');
    return {
      monthYear,
      monthShortForm,
      month: MONTH_SHORT_NAMES.indexOf(monthShortForm) + 1,
      year: 2000 + parseInt(yy, 10),
    };
  });

  return months.sort((a, b) =>
    order === 'asc'
      ? a.year !== b.year
        ? a.year - b.year
        : a.month - b.month
      : a.year !== b.year
        ? b.year - a.year
        : b.month - a.month
  );
};

// Derives the list of months that actually have data for the selected
// accounts, from each account profile's monthlyAnalytics. Sorted newest to
// oldest. Used to drive month-filter dropdowns dynamically instead of a
// hardcoded last-N-months list.
export function getMonthsListFromAccountProfiles(
  accountProfileList: any[],
  selectedAcconts: string[],
) {
  if (!accountProfileList?.length || !selectedAcconts?.length) return [];

  const monthMap = new Map<
    string,
    { monthShortForm: string; month: number; year: number }
  >();

  accountProfileList.forEach((profile: any) => {
    if (selectedAcconts.includes(profile.account?.linkedAccRef)) {
      const monthlyAnalytics = profile.monthlyAnalytics || [];
      monthlyAnalytics.forEach((item: any) => {
        const m = Number(item.month);
        const y = Number(item.year);
        if (m >= 1 && m <= 12 && y > 2000) {
          const key = `${m}${y}`;
          if (!monthMap.has(key)) {
            monthMap.set(key, {
              monthShortForm: getMonthAbbreviation(m),
              month: m,
              year: y,
            });
          }
        }
      });
    }
  });

  return Array.from(monthMap.values()).sort((a, b) => {
    if (b.year !== a.year) return b.year - a.year;
    return b.month - a.month;
  });
}

export const reversedCategoriesMapping: any = {
  salary: 'SALARY',
  emis: 'EMI',
  'loan disbursed': 'LOAN_DISBURSAL',
  'bounced i/w ecs': 'BOUNCED_IW_ECS',
  'bounce transaction': 'BOUNCE_TRANSACTION',
  'bounced i/w cheque': 'BOUNCED_IW_CHEQUE',
  'bounced o/w cheque': 'BOUNCED_OW_CHEQUE',
  'i/w cheque bounce charge': 'IW_CHEQUE_BOUNCE_CHARGE',
  'o/w cheque bounce charge': 'OW_CHEQUE_BOUNCE_CHARGE',
  'bounced i/w ecs charges': 'BOUNCED_IW_ECS_CHARGE',
  'bounce charge': 'BOUNCE_CHARGE',
  'tax payment': 'TAX_PAYMENT',
  'tax refund': 'TAX_REFUND',
  'transfer to wallet': 'TRANSFER_TO_WALLET',
  'transfer from wallet to bank': 'TRANSFER_FROM_WALLET_TO_BANK',
  'cash withdrawals': 'CASH_WITHDRAWAL',
  'below min balance': 'MINIMUM_BALANCE_CHARGE',
  'bank charges': 'BANK_CHARGE',
  'transfer to fd/rd': 'TRANSFER_TO_FD_RD',
  'app subscription': 'APP_SUBSCRIPTION',
  'interest collected': 'INTEREST_COLLECTED',
  'salary paid': 'SALARY_PAID',
  'credit card payment': 'CREDIT_CARD_PAYMENT',
  'merchant expenditure': 'MERCHANT_EXPENDITURE',
  alcohol: 'ALCOHOL',
  'small savings': 'SMALL_SAVINGS',
  'interest credit': 'INTEREST_CREDIT',
  'cash deposit': 'CASH_DEPOSIT',
  'cheque paid': 'CHEQUE_PAID',
  'cheque deposit': 'CHEQUE_DEPOSIT',
  reversal: 'REVERSAL_TXN',
  'credit sweep': 'CREDIT_SWEEP',
  'debit sweep': 'DEBIT_SWEEP',
  'cash back': 'CASH_BACK',
  refund: 'REFUND',
  subsidy: 'SUBSIDY',
  'provident fund withdrawal': 'PF_WITHDRAWAL',
  microfinance: 'MICROFINANCE',
  food: 'FOOD',
  hotel: 'HOTEL',
  transportation: 'TRANSPORTATION',
  finance: 'FINANCE',
  pets: 'PETS',
  sports: 'SPORTS',
  interior: 'INTERIOR',
  'real estate': 'REAL_ESTATE',
  travel: 'TRAVEL',
  entertainment: 'ENTERTAINMENT',
  gaming: 'GAMES',
  'gambling & betting': 'GAMBLING_AND_BETTING',
  shopping: 'SHOPPING',
  utilities: 'UTILITIES',
  bnpl: 'BNPL',
  'investment income': 'INVESTMENT_INCOME',
  'investment expense': 'INVESTMENT_EXPENSE',
  'health care': 'HEALTHCARE',
  'insurance premium': 'INSURANCE_PREMIUM',
  'insurance claimed': 'INSURANCE_CLAIMED',
  rent: 'RENT',
  'purchase by card': 'PURCHASE_BY_CARD',
  'transfer in': 'TRANSFER_IN',
  'transfer out': 'TRANSFER_OUT',
  'internal transfer': 'INTERNAL_TRANSFER',
  donation: 'DONATION',
  'personal care': 'PERSONAL_CARE',
  education: 'EDUCATION',
  settlement: 'SETTLEMENT',
  'p2p lending': 'P2P_LENDING',
  'professional service': 'PROFESSIONAL_SERVICES',
  others: 'OTHERS',
  'household expends': 'HOUSEHOLD_EXPENDS',
  'business service': 'BUSINESS_SERVICES',
  leisure: 'LEISURE',
  reimbursement: 'REIMBURSEMENT',
  'freelancing income': 'FREELANCING_INCOME',
  'merchant income': 'MERCHANT_INCOME',
  'forex transaction': 'FOREX_TXN',
  'government services': 'GOVERNMENT_SERVICES',
  'charges/fees': 'CHARGES',
  'cloud services': 'CLOUD_SERVICES',
  'fund remittance': 'FUND_REMITTANCE',
  'p2p transfer': 'P2P_TRANSFER',
  events: 'EVENTS',
  'account verification': 'ACCOUNT_VERIFICATION',
  agriculture: 'AGRICULTURE',
  legal: 'LEGAL',
  fines: 'FINES',
  'cultural organization': 'CULTURAL_ORGANIZATION',
};

export const budgetCategoriesMapping: any = {
  FOOD: 'Food',
  HOTEL: 'Hotel',
  TRANSPORTATION: 'Transportation',
  PETS: 'Pets',
  SPORTS: 'Sports',
  INTERIOR: 'Interior',
  TRAVEL: 'Travel',
  ENTERTAINMENT: 'Entertainment',
  GAMES: 'Gaming',
  SHOPPING: 'Shopping',
  UTILITIES: 'Utilities',
  HEALTHCARE: 'Health Care',
  EDUCATION: 'Education',
  HOUSEHOLD_EXPENDS: 'Household Expends',
  LEISURE: 'Leisure',
  EVENTS: 'Events',
};
export const fipIdNameMapping: any = {
  kol: '5',
  'UAT Testing-001': '123456789',
  'IndusInd Bank Ltd.': 'INDUSIND',
  'STATE BANK OF INDIA': 'sbi-fip-uat',
  FinShareBankServer: 'finsharebank',
  'Bajaj Finance Ltd.': 'bajaj-finserv-fip-uat',
  'HDFC Bank': 'HDFC-FIP',
  'ICICI Bank': 'ICICI-FIP',
  'IDFC FIRST BANK': 'idfc-first-fip-uat',
  'Axis Bank': 'AXIS001',
  'Acme Bank Ltd.': 'ACME-FIP',
  'FIPCAMS-UAT': 'FIPCAMS-UAT',
  'Finvu Bank Ltd.': 'BARB0KIMXXX',
  'Vayana Network': 'vayana',
  'Millennium Bank': 'fip@finbank',
  FDRLFIP: 'FEDERAL BANK',
  aujasbank: 'Aujas Bank',
  alt_aujasbank: 'Alt Aujas Bank',
  'KOTAK-FIP': 'Kotak Mahindra Bank',
  NSDLFIP: 'NSDL',
  'Karur Vysya Bank': 'KVB-FIP',
  'Max Life Insurance': 'mli-fip',
  'FINBIT FIP DEV': 'finbit-fip-dev',
  'FINBIT FIP UAT': 'finbit-fip-uat',
  'Fime India': 'FIP0003',
  'Indian Bank': 'IBFIP',
  'AU Small Finance Bank': 'AUBank-FIP',
  'Setu FIP': 'setu-fip',
  'City Union Bank': 'fipuat@citybank',
  'Tata Consultancy Services Ltd': 'tcs-kychub-uat-fip',
  'Pirimid FinTech': 'FIP-ID',
  'Bajaj Allianz Life Insurance': 'balic-fip',
  'Yes Bank Ltd': 'YESB-UAT-FIP',
  'YES Bank': 'YESB-UAT-FIP',
  'HDFC Life Insurance': 'fip@hdfclifeuat',
  'ICICI Lombard GICL': 'fip@icicilombarduat',
  'Bank Of Baroda': 'BARBFIP_UAT',
  'Punjab National Bank': 'PNB-UAT-FIP',
  'Canara Bank': 'fip@canarabankuat',
  'UCO Bank': 'UCOB-UAT-FIP',
  'Bank of Maharashtra': 'BOM_FIP',
  'Bank Of India': 'BOI-UAT-FIP',
  'India Overseas Bank': 'IOB-UAT-FIP',
  'Punjab and Sind Bank': 'PSFIP',
  'Central Bank': 'CENTRALFIP',
  'Fime India ALT FIP': 'FIME_FIP_ALT',
  FIME_FIP_0002: 'FIME_FIP_0002',
  'Finacus TSP FIP': 'finacus-tsp-fip-uat',
  'Digio FIP Pvt. Limited': 'DIGIO_FIP',
  'KB_Fime India': 'FIP00035',
  FIME_FIP_YF_0001: 'FIME_FIP_YF_0001',
  'FIME_ALT FIP_YF_0001': 'FIME_ALT FIP_YF_0001',
  'Aditya Birla Sun Life Insurance': 'absli-fip',
  'CAMS Mutual Fund': 'CAMS_AMC_UAT',
  'Digio FIU Pvt. Limited': 'DIGIO_FIU',
  'CAMS FinServ': 'AA00022222',
  'ICICI Prudential Life Insurance': 'iciciprudential-fip-uat',
  NPS_UAT: 'NPS_UAT',
  'Fincare Small Finance Bank': 'fip@fincaresfbuat',
  'National Securities Depository Limited': 'fip@uat.nsdl',
  ' UATNSDLFIP ': 'UATNSDLFIP',

  TNGB: 'TNGB_UAT',
  'MAHARASHTRA GRAMIN BANK': 'mh-gb-fiu',
  'Kerala Gramin Bank': 'KERFIP',
  PBGB: 'PBGB_FIP_UAT',
  'SBI Life Insurance': 'sbilife-fip',
  'Bajaj Allianz General Insurance Company': 'bagic-fip',
  'LandT Finance Limited': 'ltfs-fiu-uat',
  Simply: 'SIM001',
  AQ: 'A01',
  'Saptagiri Grameena Bank': 'SGB_FIP_UAT',
  'Air co': 'FIP01',
  QAS: 'FIP001',
  LOL: 'LO001',
  jio: 'FIPTEST1',
  HP: 'FIPTEST2',
  JIO: 'QATEST01',
  KIO: 'QATEST02',
  'Bank Ltd': 'SAHA000001',
  'Andhra Pradesh Gramin Vikas Bank': 'apgvb-fip-uat',
  kvgbank: 'kvgbank-fip-uat',
  apgb: 'apgb-fip-uat',
  'CRIF CONNECT PRIVATE LIMITED': 'CRIF-CONNECT-UAT-FIP',

  SS: 'BAO01',
  'Heath care': 'HC001',
  'Service TAX': 'FIU00020001',
  'Master Bank': 'MASTER01',
  'master heath care': 'MASTER012',
  'plan BANK': 'MASTER00123',
  'Paschim Banga Gramin Bank': 'PNB-HOUSING-FIP',
  'CRIF CONNECT PRIVATE LIMITED SECONDARY': 'CRIF-CONNECT-PRE-PRD-FIP-ALT',
  'Kfin Technologies Limited (Mutual Fund)': 'kfinmf-fip-uat',
  'Cams CRA_uat': 'Cams CRA_uat',
  'Electronic Payment & Services': 'EPSFIP001',
  'Cams RTA_uat': 'Cams RTA_uat',
  PROTEAN_FIP_UAT: 'PROTEAN_FIP_UAT',
  'Kfin Technologies Limited (NPS)': 'kfinnps-fip-uat',
  'SL_Fime India': 'FIP00039',
  FIPTallyEdge: 'FIPTALLYEDGE',
  Fedral: 'FedRal001',
  'Central Depository Services Limited': 'CDSLFIP',
  'Bank Tech': 'Bank Tech-001 #001',
  'Union Bank': 'Union-Bb',
  'AV FIP ltd': 'FIP10001768',
  'People Bank': 'Bank-001',
  'AQM Tech FIP': 'aqm-fip-uat',
  ISOLVE: 'ISOLVE-TSP-FIP',
  'Goods and Services Tax Network': 'GSTN-FIP-TEST',
  ETLIFIP_UAT: 'ETLIFIP_UAT',
  CAMSGST_UAT: 'CAMSGST_UAT',
  SIDBI: 'SIDBIFIP',
  'Add test GSTN': 'GSTN-001',
  'Dev Test gstn FI type': 'Dev Test gstn FI type',
  'UAT Test': 'UAT Test GSTN-001',
  'FIP Testing UAT': 'FIP Testing UAT-0003',
  'fip Testing UAT-0003': 'FIP testing UAT-00033',
  'uat tEST gstn-0051': 'uat tEST gstn-0051',
  'uat tEST gstr-00511': 'uat tEST gstr-00511',
  'Rajasthan Marudhara Gramin Bank': 'rmgbrrb-fip-uat',
  'Arunachal Pradesh Rural Bank': 'aprbrrb-fip-uat',
  'Mizoram Rural Bank': 'mzrbrrb-fip-uat',
  'Chhattisgarh Rajya Gramin Bank': 'crgbrrb-fip-uat',
  'Ellaquai Dehati Bank': 'edbrrb-fip-uat',
  'Meghalaya Rural Bank': 'merbrrb-fip-uat',
  'Madhyanchal Gramin Bank': 'mgbrrb-fip-uat',
  'NAGALAND RURAL BANK': 'nrbrrb-fip-uat',
  'UTTARAKHAND GRAMIN BANK': 'utgbrrb-fip-uat',
  'UAT TEST ': 'UAT TEST-0032',
  'JHARKHAND RAJYA GRAMIN BANK': 'jrgbrrb-fip-uat',
  'Saurashtra Gramin Bank': 'sgbrrb-fip-uat',
  'Telangana Gramin Bank': 'tgbhyd-fip-uat',
  'Vidharbha Konkan Gramin Bank': 'vkgbrrb-fip-uat',
  'KARNATAKA VIKAS GRAMEENA BANK': 'kvgbrrb-fip-uat',
  'Haryana Gramin Bank': 'hgbrrb-fip-uat',
  'FIU BANK': 'FIU BANK',
  'Fip Bank': 'Fip Bank',
  'No value': 'FIP 2020',
  'AGFIP - Test': 'AGFIP - Test',
  'DFI Bank': 'FIP DFIBANK',
  'DFI FIU Bank': 'DFI FIU Bank',
  'AFC FIP LTD': 'AFC FIP LTD',
  'IDFC 01': 'FIPFIP2022',
  'FIP 2023': 'FIP 2023',
  'AFC Bank': 'AFC FIP',
  FIPCAMS: 'FIPCAMS',
  FIP21: 'FIP21',
  'SSgA FIP': 'FIP A01',
  'Indian Fip bank': 'Indian Fip bank',
  'AMC Fip bank': 'AMC Fip bank',
  'Fip bank name': 'Fip bank name',
  'Tirumala Fip Bank': 'Tirumala Fip Bank',
  'IDF Bank': 'IDF Bank',
  '5 Fip bank': '5 Fip bank',
  'Illumina Fip Bank': 'Illumina Fip Bank',
  Matrimony: 'Matrimony',
  FIPSYS: 'FIPSYS',
  '7E FIP': '7E FIP',
  KAPP: 'KAPP',
  HSS: 'HSS',

  'PPFAS Mutual Fund': 'PPFAS Mutual Fund',
  FIBN: 'FIBN',
  Kavi: 'Kavi',
  'Mezan bank': 'Mezan bank',
  'PNB HOUSING BANK': 'PNB HOUSING BANK',
  'HDFC HOME LOAN': 'HDFC HOME LOAN',
  'Bajaj Finance': 'Bajaj Finance',
  'FIP I_S FIP TESTING UAT-0003': 'FIP I_S FIP TESTING UAT-0003',

  AVP: 'AVP',
};

export const transactionCategoriesLogos: any = {
  ACCOUNT_VERIFICATION: 'Banking and Finance',
  AGRICULTURE: 'Shopping',
  ALCOHOL: 'food and drinks',
  APP_SUBSCRIPTION: 'Apps and Software',
  BANK_CHARGE: 'Bank Charges',
  BNPL: 'loans',
  BOUNCE_CHARGE: 'Bank Charges',
  BOUNCE_TRANSACTION: 'Bank Charges',
  BOUNCED_IW_CHEQUE: 'Bank Charges',
  BOUNCED_IW_ECS: 'Bank Charges',
  BOUNCED_IW_ECS_CHARGE: 'Bank Charges',
  BOUNCED_OW_CHEQUE: 'Bank Charges',
  BUSINESS_SERVICES: 'services',
  CASH_BACK: 'Reversals and Refunds',
  CASH_DEPOSIT: 'cash transaction',
  CASH_WITHDRAWAL: 'cash transaction',
  CHARGES: 'Bank Charges',
  CHEQUE_DEPOSIT: 'Banking and Finance',
  CHEQUE_PAID: 'Banking and Finance',
  CLOUD_SERVICES: 'Apps and Software',
  CREDIT_CARD_PAYMENT: 'Credit Card Bill',
  CREDIT_SWEEP: 'Banking and Finance',
  CULTURAL_ORGANIZATION: 'health and wellness',
  DEBIT_SWEEP: 'Banking and Finance',
  DONATION: 'donation',
  EDUCATION: 'education',
  EMI: 'loans',
  ENTERTAINMENT: 'entertainment',
  EVENTS: 'entertainment',
  FINANCE: 'Banking and Finance',
  FINES: 'bills',
  FOOD: 'food and drinks',
  FOREX_TXN: 'Banking and Finance',
  FUND_REMITTANCE: 'Banking and Finance',
  GAMBLING_AND_BETTING: 'entertainment',
  GAMES: 'entertainment',
  GOVERNMENT_SERVICES: 'govt',
  HEALTHCARE: 'health and wellness',
  HOTEL: 'food and drinks',
  HOUSEHOLD_EXPENDS: 'household',
  INSURANCE_CLAIMED: 'insurance',
  INSURANCE_PREMIUM: 'insurance',
  INTEREST_COLLECTED: 'interest',
  INTEREST_CREDIT: 'interest',
  INTERIOR: 'Shopping',
  INTERNAL_TRANSFER: 'self transfer',
  INVESTMENT_EXPENSE: 'investment',
  INVESTMENT_INCOME: 'investment liquidation',
  IW_CHEQUE_BOUNCE_CHARGE: 'Bank Charges',
  LEGAL: 'services',
  LEISURE: 'entertainment',
  LOAN_DISBURSAL: 'loans',
  MERCHANT_EXPENDITURE: 'general transactions',
  MERCHANT_INCOME: 'general transactions',
  MICROFINANCE: 'Banking and Finance',
  MINIMUM_BALANCE_CHARGE: 'Bank Charges',
  OTHERS: 'OTHERS',
  OW_CHEQUE_BOUNCE_CHARGE: 'Bank Charges',
  P2P_LENDING: 'loans',
  P2P_TRANSFER: 'person',
  PERSONAL_CARE: 'health and wellness',
  PETS: 'Shopping',
  PROFESSIONAL_SERVICES: 'services',
  PURCHASE_BY_CARD: 'debit Card transactions',
  REAL_ESTATE: 'bills',
  REFUND: 'reversals and refunds',
  REIMBURSEMENT: 'reversals and refunds',
  RENT: 'rent',
  REVERSAL_TXN: 'reversals and refunds',
  SALARY: 'Salary',
  SALARY_PAID: 'Salary',
  SHOPPING: 'Shopping',
  SMALL_SAVINGS: 'investment',
  SPORTS: 'Shopping',
  SUBSIDY: 'banking and finance',
  TAX_PAYMENT: 'govt',
  TAX_REFUND: 'govt',
  TRANSFER_FROM_WALLET_TO_BANK: 'wallet and digital payments',
  TRANSFER_IN: 'person',
  TRANSFER_OUT: 'person',
  TRANSFER_TO_WALLET: 'wallet and digital payments',
  TRANSPORTATION: 'travel',
  TRAVEL: 'travel',
  UTILITIES: 'bills',
};

export const nudgesLogo: any = {
  INTEREST_CREDITED: 'interest',
  BANK_CHARGES_DETECTED: 'Bank Charges',
  DIVIDENDS_RECEIVED: 'investment',
  SALARY_CREDITED: 'Salary',
  UPCOMING_EMI_REMINDER: 'loans',
  TRAVEL_DETECTED: 'travel',
  UPCOMING_SIP_INVESTMENT: 'investment',
  MINIMUM_BALANCE_CHARGES_DETECTED: 'Bank Charges',
  CASHBACK_DETECTED: 'reversals and refunds',
  REFUND_CREDITED: 'reversals and refunds',
  REFUND_DETECTED: 'reversals and refunds',
};

export function getNudgeDescription(nudge: any) {
  switch (nudge.category) {
    case 'INTEREST_CREDITED':
      return 'You have received ₹XXX as interest from your Bank(s) this financial year';
    case 'BANK_CHARGES_DETECTED':
      if (getTenant() === TenantType.BOB || getTenant() === TenantType.BOB_V2) {
        return 'You have paid ₹XXX as bank charges';
      }
      return 'You have paid ₹XXX as bank charges in the last 30 days';
    case 'DIVIDENDS_RECEIVED':
      return 'You have received ₹XXX as dividend from your investments this financial year';
    case 'SALARY_CREDITED':
      if (getTenant() === TenantType.BOB || getTenant() === TenantType.BOB_V2) {
        return 'You received ₹XXX as recent salary';
      }
      return 'You received ₹XXX as salary in the last 30 days';
    case 'UPCOMING_EMI_REMINDER':
      return 'You have an upcoming EMI of ₹XXX due this month';
    case 'TRAVEL_DETECTED':
      if (getTenant() === TenantType.BOB || getTenant() === TenantType.BOB_V2) {
        return 'You have spent ₹XXX on Travel';
      }
      return 'You have spent ₹XXX on Travel in the last 30 days';
    case 'UPCOMING_SIP_INVESTMENT':
      return 'You have an SIP of ₹XXX due this month';
    case 'MINIMUM_BALANCE_CHARGES_DETECTED':
      if (getTenant() === TenantType.BOB || getTenant() === TenantType.BOB_V2) {
        return 'You have paid ₹XXX as minimum balance charges';
      }
      return 'You have paid ₹XXX as minimum balance charges in the last 30 days';
    case 'CASHBACK_DETECTED':
      if (getTenant() === TenantType.BOB || getTenant() === TenantType.BOB_V2) {
        return 'You have received ₹XXX as cashback';
      }
      return 'You have received ₹XXX as cashback in the last 30 days';
    case 'REFUND_CREDITED':
      if (getTenant() === TenantType.BOB || getTenant() === TenantType.BOB_V2) {
        return 'You have received ₹XXX as refund';
      }
      return 'You have received ₹XXX as refund in the last 30 days';
    case 'REFUND_DETECTED':
      if (getTenant() === TenantType.BOB || getTenant() === TenantType.BOB_V2) {
        return 'You have received ₹XXX as refund';
      }
      return 'You have received ₹XXX as refund in the last 30 days';
    case 'MUTUAL_FUND_REDEMPTION':
      return 'You have received ₹XXX from mutual fund redemption';
    default:
      if (typeof nudge.description === 'string') return formatSnakeCaseToDefault(nudge.description);
      if (typeof nudge.description === 'object' && nudge.description?.nudgeText)
        return nudge.description.nudgeText;
      if (typeof nudge.description === 'object' && nudge.description?.body)
        return nudge.description.body;
      return '';
  }
}

function formatSnakeCaseToDefault(res: string) {
  if (!res.includes('_')) return res;

  return res
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function getNudgeBodyText(nudge: any): string {
  if (!nudge.body) return '';
  if (typeof nudge.body === 'string') return nudge.body;
  if (typeof nudge.body === 'object' && nudge.body.body) return nudge.body.body;
  if (typeof nudge.body === 'object' && nudge.body.nudgeText)
    return nudge.body.nudgeText;
  return '';
}

const bankNameMapping: { [key: string]: string } = {
  'au small finance bank': 'AU Small Finance Bank',
  'axis bank': 'Axis Bank',
  'bank of baroda': 'Bank of Baroda',
  'bank of india': 'Bank Of India',
  'bank of maharashtra': 'Bank of Maharashtra',
  'city union bank': 'City Union Bank',
  'canara bank': 'Canara Bank',
  'fincare small finance bank': 'Fincare Small Finance Bank',
  'federal bank': 'Federal Bank',
  'hdfc bank': 'HDFC Bank',
  'icici bank': 'ICICI Bank',
  'indian bank': 'Indian Bank',
  'indian overseas bank': 'Indian Overseas Bank',
  'indusind bank ltd.': 'IndusInd Bank',
  'idbi bank ltd.': 'IDBI Bank',
  'idfc first bank': 'IDFC First Bank',
  'karur vysya bank': 'Karur Vysya Bank',
  'karnataka bank ltd': 'Karnataka Bank',
  'kotak mahindra bank': 'Kotak Mahindra Bank',
  'punjab national bank': 'Punjab National Bank',
  'rbl bank limited': 'RBL Bank',
  'state bank of india': 'State Bank of India',
  'the south indian bank ltd': 'The South Indian Bank',
  'uco bank': 'UCO Bank',
  'union bank of india': 'Union Bank Of India',
  'yes bank ltd': 'Yes Bank',
};

// Utility function to get the properly cased bank name
export function getFipName(inputName: string | null | undefined): string {
  if (typeof inputName !== 'string') {
    return '';
  }
  const normalizedInput = inputName.toLowerCase();
  return bankNameMapping[normalizedInput] || inputName;
}

/**
 * Formats a date string to display in the format "14 October, 2024"
 * @param dateStr The date string to format
 * @returns Formatted date string or "NA" if the date is invalid or null
 */
export function formatDates(dateStr: string | null | undefined): string {
  if (!dateStr) return 'NA';
  try {
    return dayjs(dateStr).format('DD MMMM, YYYY');
  } catch (error) {
    captureException(error);
    return 'NA';
  }
}

export const showSessionTimeoutModal = () => {
  Modal.confirm({
    title: 'Session Expired',
    content: 'Your session has expired. Please log in again.',
    okText: 'Close',
    cancelButtonProps: { style: { display: 'none' } },
    centered: true,
    okButtonProps: {
      style: {
        backgroundColor: 'var(--selected-tag-category)',
        borderColor: 'var(--selected-tag-category)',
        color: '#fff',
        borderRadius: 16,
      },
    },
    onOk: () => {
      window.close();
    },
  });
};
export async function retryAsync<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 500,
  retryOn?: (error: any) => boolean
): Promise<T> {
  let lastError: any;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Check if we should retry based on the error
      if (retryOn && !retryOn(error)) {
        throw error;
      }

      // If this was the last attempt, throw the error
      if (attempt === maxRetries) {
        throw error;
      }

      // Log retry attempt in development/UAT
      if (process.env.REACT_APP_IS_UAT_ENV === 'true') {
        console.log(
          `Retry attempt ${attempt}/${maxRetries} after ${delay}ms...`
        );
      }

      // Wait before retrying (exponential backoff)
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}

export const getTimeUntilRefresh = (
  refreshTime: string | null,
  defaultLabel: string = '30 days'
): string => {
  if (!refreshTime) return defaultLabel;
  const nextRefreshDate = dayjs(refreshTime);
  const now = dayjs();
  const days = nextRefreshDate.diff(now, 'days');
  if (days < 1) {
    const hours = nextRefreshDate.diff(now, 'hours');
    return hours < 1 ? '1 hour' : hours + ' hours';
  }
  return days === 1 ? '1 day' : days + ' days';
};

export const checkIsRefreshAvailable = (
  refreshTime: string | null
): boolean => {
  if (!refreshTime) return false;
  const nextRefreshDate = dayjs(refreshTime);
  const now = dayjs();
  return now.isAfter(nextRefreshDate) || now.isSame(nextRefreshDate);
};

export const getRefreshInfoForFiTypes = (
  accountDetails: AccountDetails[],

  fiTypes: FIType[]
): {
  lastUpdated: string | null;
  nextRefreshTime: string | null;
  nextManualRefreshTime: string | null;
} => {
  const relevantAccounts =
    accountDetails?.filter(
      (acc) =>
        fiTypes.includes(acc.fiType) && acc.status === AccountStatusType.ACTIVE
    ) || [];

  const lastUpdated =
    relevantAccounts
      .map((acc) => acc.lastRawDataFetchedAt)
      .filter((value) => value != null)
      .sort()
      .pop() || null;

  const nextRefresh =
    relevantAccounts
      .map((acc) => acc.nextRefreshTime)
      .filter((value) => value != null)
      .sort()
      .pop() || null;

  const nextManualRefresh =
    relevantAccounts
      .map((acc) => acc.nextManualRefreshTime)
      .filter((value) => value != null)
      .sort()
      .pop() || null;

  return {
    lastUpdated,
    nextRefreshTime: nextRefresh,
    nextManualRefreshTime: nextManualRefresh,
  };
};

export const getEarliestRefreshTime = (
  accountDetails: AccountDetails[],
  fiTypes: FIType[]
): string | null => {
  const activeAccounts =
    accountDetails?.filter(
      (acc) =>
        fiTypes.includes(acc.fiType) && acc.status === AccountStatusType.ACTIVE
    ) || [];

  const allTimes = activeAccounts
    .flatMap((acc) => [acc.nextRefreshTime, acc.nextManualRefreshTime])
    .filter((value): value is string => value != null);

  if (allTimes.length === 0) return null;

  return allTimes.sort()[0];
};

export const getEarliestManualRefreshTime = (
  accountDetails: AccountDetails[],
  fiTypes: FIType[]
): string | null => {
  const times = (accountDetails ?? [])
    .filter(
      (acc) => fiTypes.includes(acc.fiType) && acc.status === AccountStatusType.ACTIVE,
    )
    .map((acc) => acc.nextManualRefreshTime)
    .filter((t): t is string => t != null)
    .sort();
  return times[0] ?? null;
};

export const isRefreshDoneForFiTypes = (
  accountDetails: AccountDetails[],
  fiTypes: FIType[]
): boolean => {
  const relevantAccounts =
    accountDetails?.filter(
      (acc) =>
        fiTypes.includes(acc.fiType) && acc.status === AccountStatusType.ACTIVE
    ) || [];

  if (relevantAccounts.length === 0) return true;

  return relevantAccounts.every(
    (acc) => acc.refreshStatus !== RefreshDataFetchStatus.PENDING
  );
};

// FI statuses that mean the FIP could not share data for the account
const DATA_FETCH_FAILED_FI_STATUSES = [
  FIStatus.DATA_NOT_RETURNED_IN_TIME,
  FIStatus.TIMEOUT,
  FIStatus.DENIED,
];

export const isAccountDataFetchFailed = (acc: AccountDetails): boolean =>
  acc.lastAnalyzedAt === null &&
  DATA_FETCH_FAILED_FI_STATUSES.includes(acc.lastRequestFIStatus);

export const isDataFetchFailedForFiTypes = (
  accountDetails: AccountDetails[],
  fiTypes: FIType[]
): boolean => {
  const activeAccounts =
    accountDetails?.filter(
      (acc) =>
        fiTypes.includes(acc.fiType) && acc.status === AccountStatusType.ACTIVE
    ) || [];

  if (activeAccounts.length === 0) return false;

  return activeAccounts.every(isAccountDataFetchFailed);
};

export const getNextManualRefreshText = (
  nextManualRefreshTime: string | null
): string | null => {
  if (!nextManualRefreshTime) return null;
  if ( getTenant() === TenantType.SIB ) { 
    return `Next manual refresh at ${dayjs(nextManualRefreshTime).format(DATE_FORMATS.DD_MMM_YY_TIME)}`;
  }
  return `Next refresh at ${dayjs(nextManualRefreshTime).format(DATE_FORMATS.DD_MMM_YYYY_TIME)}`;
};

export const getLastUpdatedText = (
  lastUpdatedTime: string | null
): string | null => {
  if (!lastUpdatedTime) return null;
  const lastUpdatedDate = dayjs(lastUpdatedTime);
  if ( getTenant() === TenantType.SIB ) {
    const now = dayjs();
    const hoursDiff = now.diff(lastUpdatedDate, 'hours');
    if (hoursDiff < 24) {
      return hoursDiff <= 1
        ? 'Updated 1 hour ago'
        : `Updated ${hoursDiff} hours ago`;
    }
    return `Updated on ${lastUpdatedDate.format(DATE_FORMATS.DD_MMM_YYYY)}`;
  }
  return `Updated on ${lastUpdatedDate.format(DATE_FORMATS.DD_MMM_YYYY_TIME)}`;
};

export const checkIsNumeric = (value: string | number | null | undefined) => {
  const parsed = Number(value);
  return isNaN(parsed) ? 0 : parsed;
};

export const calculateMonths = (date?: string): number | undefined => {
  if (!date) return undefined;

  const opening = new Date(date);
  const now = new Date();

  return (
    (now.getFullYear() - opening.getFullYear()) * 12 +
    (now.getMonth() - opening.getMonth())
  );
};

export const getSchemeCategory = (
  schemeName = ''
): NPSSchemeCategory | null => {
  const match = schemeName.match(/.+\s([ECGA])/);
  if (!match) return null;
  return match[1] as NPSSchemeCategory;
};

export const getAssetClass = (category: string): string => {
  switch (category) {
    case 'E':
      return NPS_CATEGORY['E'].toUpperCase();
    case 'C':
      return NPS_CATEGORY['C'].toUpperCase().replace(' ', '_');
    case 'G':
      return NPS_CATEGORY['G'].toUpperCase().replace(' ', '_');
    default:
      return NPS_CATEGORY['A'].toUpperCase().replace(' ', '_');
  }
};

export const formatDateWithOrdinal = (
  date: Date
): Record<string, number | string> => {
  const day = date.getDate();
  const suffix = ['th', 'st', 'nd', 'rd'];
  const v = day % 100;
  const ordinal = suffix[(v - 20) % 10] || suffix[v] || suffix[0];
  return { day, ordinal };
};

export const getEarliestRefreshTimeforSIB = (
  accountDetails: AccountDetails[],
  fiTypes: FIType[]
): string | null => {
  const activeAccounts =
    accountDetails?.filter(
      (acc) =>
        fiTypes.includes(acc.fiType) &&
        acc.status === AccountStatusType.ACTIVE &&
        acc.lastRequestFIStatus !== FIStatus.DENIED
    ) || [];

  const allTimes = activeAccounts
    .map((acc) => acc.nextManualRefreshTime)
    .filter((value): value is string => value != null);

  if (allTimes.length === 0) return null;

  return allTimes.sort()[0];
};

export const getMaximumLastRefreshedTime = (
  accountDetails: AccountDetails[],
  fiTypes: FIType[]
): string | null => {
  const activeAccounts =
    accountDetails?.filter(
      (acc) => fiTypes.includes(acc.fiType) && acc.status === AccountStatusType.ACTIVE
    ) || [];

  const allTimes = activeAccounts
    .map((acc) => acc.lastRawDataFetchedAt)
    .filter((value): value is string => value != null);

  if (allTimes.length === 0) return null;

  return allTimes.sort().pop() as string;
};
